import type { Reward } from 'features/reward/types';

import { LoyaltyInRestaurantIncentiveType as ICartEntryType } from 'generated/graphql-gateway';
import type {
  LoyaltyOffer as IOfferCartIncentive,
  LoyaltyReward as IRewardCartIncentive,
  LoyaltyEngineReward,
} from 'state/loyalty/types';

export type {
  LoyaltyOffer as IOfferCartIncentive,
  LoyaltyReward as IRewardCartIncentive,
} from 'state/loyalty/types';
export { ICartEntryType };

// helper type
type ArrayElement<ArrayType extends unknown[]> = ArrayType extends (infer ElementType)[]
  ? ElementType
  : never;

export type IEngineRewardCartIncentive = NonNullable<LoyaltyEngineReward>;
export type ILegacyCartEntryDetailsReward = {
  source?: 'legacy';
  reward: IRewardCartIncentive;
  engineReward: IEngineRewardCartIncentive;
};
export type IServiceCartEntryDetailsReward = {
  source: 'service';
  reward: Reward;
};
export type ICartEntryDetailsReward =
  | ILegacyCartEntryDetailsReward
  | IServiceCartEntryDetailsReward;
export type ICartEntryDetailsOffer = {
  offer: IOfferCartIncentive;
};

export type ICartEntryDetails = ICartEntryDetailsReward | ICartEntryDetailsOffer;

export interface ICartEntryBase {
  // a unique id to reference the cart entry
  referenceId: string;
  // cart entry quantity
  quantity: number;
  // cart entry type, it represents the redemption object that was used to create the cart entry
  type: ICartEntryType;
  // object to store all the needed data to help the item processing step
  details: ICartEntryDetails;
}

export interface ICartEntryReward extends ICartEntryBase {
  type: ICartEntryType.REWARD;
  details: ICartEntryDetailsReward;
}
export interface ICartEntryOffer extends ICartEntryBase {
  type: ICartEntryType.OFFER;
  details: ICartEntryDetailsOffer;
}

export interface ICreateLegacyCartEntryRewardParams {
  source?: 'legacy';
  reward: IRewardCartIncentive;
  engineReward: IEngineRewardCartIncentive;
}
export interface ICreateServiceCartEntryRewardParams {
  source: 'service';
  reward: Reward;
}
export type ICreateCartEntryRewardParams =
  | ICreateLegacyCartEntryRewardParams
  | ICreateServiceCartEntryRewardParams;
export interface ICreateCartEntryOfferParams {
  offer: IOfferCartIncentive;
}
export type ICreateCartEntryParams = ICreateCartEntryRewardParams | ICreateCartEntryOfferParams;

export interface ICreateCartEntryOptions {
  initialQuantity?: number;
}

export type ICart = (ICartEntryReward | ICartEntryOffer)[];
export type ILegacyCartEntry = ArrayElement<ICart>;

// type guards
export const isOffer = (entity: any): entity is IOfferCartIncentive =>
  entity?._type === 'systemwideOffer' || entity?._type === 'configOffer';
export const isReward = (entity: any): entity is IRewardCartIncentive =>
  entity?.__typename?.toLowerCase() === 'reward' && !!entity?._id;
export const isEngineReward = (entity: any): entity is IEngineRewardCartIncentive =>
  entity?.__typename?.toLowerCase() === 'loyaltyreward' && !!entity?.id;

export const isServiceRewardDetails = (
  details: ICartEntryDetailsReward
): details is IServiceCartEntryDetailsReward => details.source === 'service';

export const getInRestaurantRewardId = (details: ICartEntryDetailsReward): string =>
  isServiceRewardDetails(details) ? details.reward.id : details.engineReward.id;

export const getInRestaurantRewardDocumentId = (details: ICartEntryDetailsReward): string =>
  isServiceRewardDetails(details) ? details.reward.cmsId ?? details.reward.id : details.reward._id;

export const getInRestaurantRewardBenefitId = (
  details: ICartEntryDetailsReward
): string | undefined =>
  isServiceRewardDetails(details)
    ? details.reward.rewardBenefitId
    : details.engineReward.rewardBenefitId;

export const getInRestaurantRewardPointCost = (details: ICartEntryDetailsReward): number =>
  isServiceRewardDetails(details) ? details.reward.pointCost : details.engineReward.pointCost;
